import React, { Fragment, useCallback, useEffect, useState } from 'react';
import Multilingual from '~~utils/Multilingual';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import { gameStatus, gameFinishedStatus } from '../Const';
import { GameSectionDiv, StyledForm } from '../styled';
import { useTournamentState } from '../useTournament';
import { parseScoreBoardData } from '../service';
import { PlayerResource } from '~~apis/resource';

const teamChangeable = (game, side) => {
  const team = game[side];
  return !team.record_battings && !team.record_pitchings && !team.record_fieldings && team.records.length === 0;
};

const GameScoreBoardSection = ({ game, onChange, apiInProgress }) => {
  const { tournamentInfo, teams, gameGroups } = useTournamentState();

  const [currentGameData, setCurrentGameData] = useState(parseScoreBoardData(game));

  const [awayTeamPlayers, setAwayTeamPlayers] = useState([]);
  const [homeTeamPlayers, setHomeTeamPlayers] = useState([]);

  const fetchTeamPlayers = useCallback(
    (awayTeamUniqid, homeTeamUniqid) => {
      if (awayTeamUniqid) {
        PlayerResource.getPlayersInSeasonTeam({ seasonUniqid: tournamentInfo.uniqid, seasonTeamUniqid: awayTeamUniqid })
          .then(({ data }) => {
            setAwayTeamPlayers(data);
          })
          .catch(() => {});
      } else {
        setAwayTeamPlayers([]);
      }
      if (homeTeamUniqid) {
        PlayerResource.getPlayersInSeasonTeam({ seasonUniqid: tournamentInfo.uniqid, seasonTeamUniqid: homeTeamUniqid })
          .then(({ data }) => {
            setHomeTeamPlayers(data);
          })
          .catch(() => {});
      } else {
        setHomeTeamPlayers([]);
      }
    },
    [tournamentInfo],
  );

  useEffect(() => {
    fetchTeamPlayers(
      teams.find((t) => t.name === currentGameData.away.team)?.uniqid,
      teams.find((t) => t.name === currentGameData.home.team)?.uniqid,
    );
  }, [currentGameData, fetchTeamPlayers, teams]);

  useEffect(() => {
    setCurrentGameData(parseScoreBoardData(game));
  }, [game]);

  useEffect(() => {
    const originGameData = JSON.stringify(parseScoreBoardData(game));
    if (originGameData === JSON.stringify(currentGameData)) {
      onChange(null);
      return;
    }

    onChange(currentGameData);
    return;
  }, [currentGameData, game, onChange]);

  const handleInputOnChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    const indentedKeys = name.split('.');
    const indentedAmount = indentedKeys.length;

    let targetObject = { ...currentGameData };
    switch (indentedAmount) {
      case 3:
        targetObject = {
          ...currentGameData,
          [indentedKeys[0]]: {
            ...currentGameData[indentedKeys[0]],
            [indentedKeys[1]]: {
              ...currentGameData[indentedKeys[0]][indentedKeys[1]],
              [indentedKeys[2]]: value,
            },
          },
        };
        break;
      case 2:
        targetObject = {
          ...currentGameData,
          [indentedKeys[0]]: {
            ...currentGameData[indentedKeys[0]],
            [indentedKeys[1]]: value,
          },
        };
        break;
      default:
        targetObject = {
          ...currentGameData,
          [indentedKeys[0]]: value,
        };
        break;
    }

    if (['away.scoresString', 'home.scoresString'].includes(name)) {
      let runs = 0;
      const scores = value.split(',');
      scores.forEach((score) => {
        const scoreInt = parseInt(score);
        if (Number.isNaN(scoreInt)) {
          return;
        }

        runs += scoreInt;
      });

      const side = name.slice(0, 4);
      targetObject[side].runs = runs.toString();
    }

    if (name === 'away.team') {
      targetObject.away.scheduled_sp_uniqid = '';
    }
    if (name === 'home.team') {
      targetObject.home.scheduled_sp_uniqid = '';
    }

    setCurrentGameData(targetObject);
  };

  return (
    <GameSectionDiv>
      <h3 className="section--title">{Multilingual(`GAME.SCORE_BOARD`)}</h3>
      <StyledForm onSubmit={(e) => e.preventDefault()}>
        <div className="box">
          {(!teamChangeable(game, 'away') || !teamChangeable(game, 'home')) && (
            <div className="section--description">
              {Multilingual(`TOURNAMENT.SCORE_BOARD_TEAM_DISABLE_DESCRIPTION`)}
            </div>
          )}
          <div className="input-field --multi-input">
            <div>
              {/* 客場球隊 */}
              <label>{Multilingual(`GAME.AWAY`)}</label>
              <Select
                width="100%"
                name="away.team"
                value={currentGameData.away.team}
                onChange={handleInputOnChange}
                disabled={!teamChangeable(game, 'away') || apiInProgress}
              >
                <option value="NONE">{Multilingual(`GAME.TEAM_NONE`)}</option>
                {teams.map((team) => (
                  <option key={team.name} value={team.name}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              {/* 主場球隊 */}
              <label>{Multilingual(`GAME.HOME`)}</label>
              <Select
                width="100%"
                name="home.team"
                value={currentGameData.home.team}
                onChange={handleInputOnChange}
                disabled={!teamChangeable(game, 'home') || apiInProgress}
              >
                <option value="NONE">{Multilingual(`GAME.TEAM_NONE`)}</option>
                {teams.map((team) => (
                  <option key={team.name} value={team.name}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="input-field --multi-input">
            <div>
              <label>客隊先發投手</label>
              <Select
                width="100%"
                name="away.scheduled_sp_uniqid"
                value={currentGameData.away.scheduled_sp_uniqid}
                onChange={handleInputOnChange}
                disabled={currentGameData.away.team === 'NONE' || apiInProgress}
              >
                <option value="">未指定</option>
                {awayTeamPlayers.map((player) => (
                  <option key={player.uniqid} value={player.uniqid}>
                    {`#${player.number} ${player.name}`}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label>主隊先發投手</label>
              <Select
                width="100%"
                name="home.scheduled_sp_uniqid"
                value={currentGameData.home.scheduled_sp_uniqid}
                onChange={handleInputOnChange}
                disabled={currentGameData.home.team === 'NONE' || apiInProgress}
              >
                <option value="">未指定</option>
                {homeTeamPlayers.map((player) => (
                  <option key={player.uniqid} value={player.uniqid}>
                    {`#${player.number} ${player.name}`}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="input-field --multi-input">
            {!tournamentInfo.auto_game_seq && (
              <div>
                {/* 比賽編號 */}
                <label>{Multilingual(`GAME.SEQ`)}</label>
                <Input
                  width="100%"
                  name="seq"
                  value={currentGameData.seq}
                  onChange={handleInputOnChange}
                  disabled={apiInProgress}
                />
              </div>
            )}
            <div>
              {/* 比賽分類 */}
              <label>{Multilingual(`GAME.GROUP`)}</label>
              <Select
                width="100%"
                name="group"
                value={currentGameData.group}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              >
                <option value="NONE">{Multilingual(`GAME.GROUP_NONE`)}</option>
                {gameGroups.map((group) => (
                  <option key={group.name} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              {/* 預計開始時間 */}
              <label>{Multilingual(`GAME.SCHEDULED_START_AT`)}</label>
              <Input
                width="100%"
                name="info.time.scheduled_start_at"
                placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                value={currentGameData.info.time.scheduled_start_at}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              {/* 地點 */}
              <label>{Multilingual(`GAME.LOCATION`)}</label>
              <Select
                width="100%"
                name="info.location_uniqid"
                value={currentGameData.info.location_uniqid}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              >
                <option value="NONE">{Multilingual(`GAME.LOCATION_NONE`)}</option>
                {tournamentInfo.locations.map((location) => (
                  <option key={location.uniqid} value={location.uniqid}>
                    {`${location.city}｜${location.name}`}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="input-field --multi-input">
            <div>
              {/* 狀態 */}
              <label>{Multilingual(`GAME.STATUS.WORDING`)}</label>
              <Select
                width="100%"
                name="info.status"
                value={currentGameData.info.status}
                onChange={handleInputOnChange}
                disabled={!teamChangeable(game, 'away') || !teamChangeable(game, 'home') || apiInProgress}
              >
                {gameStatus.map((status) => (
                  <option key={status} value={status}>
                    {Multilingual(`GAME.STATUS.${status}`)}
                  </option>
                ))}
              </Select>
            </div>
            {currentGameData.info.status === 'PLAYING' && (
              <Fragment>
                <div>
                  {/* 開始時間 */}
                  <label>{Multilingual(`GAME.STARTED_AT`)}</label>
                  <Input
                    width="100%"
                    name="info.time.started_at"
                    placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                    value={currentGameData.info.time.started_at}
                    onChange={handleInputOnChange}
                    disabled={apiInProgress}
                  />
                </div>
              </Fragment>
            )}
            {currentGameData.info.status === 'FINISHED' && (
              <Fragment>
                <div>
                  {/* 完賽狀態 */}
                  <label>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</label>
                  <Select
                    width="100%"
                    name="info.finished_status"
                    value={currentGameData.info.finished_status}
                    onChange={handleInputOnChange}
                    disabled={apiInProgress}
                  >
                    <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                    {gameFinishedStatus.map((status) => (
                      <option key={status} value={status}>
                        {Multilingual(`GAME.FINISHED_STATUS.${status}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  {/* 開始時間 */}
                  <label>{Multilingual(`GAME.STARTED_AT`)}</label>
                  <Input
                    width="100%"
                    name="info.time.started_at"
                    placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                    value={currentGameData.info.time.started_at}
                    onChange={handleInputOnChange}
                    disabled={apiInProgress}
                  />
                </div>
                <div>
                  {/* 結束時間 */}
                  <label>{Multilingual(`GAME.ENDED_AT`)}</label>
                  <Input
                    width="100%"
                    name="info.time.ended_at"
                    placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                    value={currentGameData.info.time.ended_at}
                    onChange={handleInputOnChange}
                    disabled={apiInProgress}
                  />
                </div>
              </Fragment>
            )}
          </div>
          {currentGameData.info.status === 'FINISHED' && (
            <Fragment>
              <div className="input-field --multi-input">
                <div>
                  {/* 客場得分 */}
                  <label>{`${Multilingual(`GAME.AWAY`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                  <Input
                    width="100%"
                    type="number"
                    name="away.runs"
                    value={currentGameData.away.runs}
                    min="0"
                    onChange={handleInputOnChange}
                    disabled={!currentGameData.info.runs_without_innings || apiInProgress}
                  />
                </div>
                <div>
                  {/* 主場得分 */}
                  <label>{`${Multilingual(`GAME.HOME`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                  <Input
                    width="100%"
                    type="number"
                    name="home.runs"
                    value={currentGameData.home.runs}
                    min="0"
                    onChange={handleInputOnChange}
                    disabled={!currentGameData.info.runs_without_innings || apiInProgress}
                  />
                </div>
                <div>
                  {/* 局數設定 */}
                  <label>{Multilingual(`GAME.FLAG.RUNS_WITHOUT_INNINGS`)}</label>
                  <input
                    type="checkbox"
                    checked={!currentGameData.info.runs_without_innings}
                    onChange={(e) =>
                      handleInputOnChange({
                        target: {
                          name: 'info.runs_without_innings',
                          value: !currentGameData.info.runs_without_innings,
                        },
                      })
                    }
                    disabled={apiInProgress}
                  />
                </div>
              </div>
              {!currentGameData.info.runs_without_innings && (
                <Fragment>
                  <div className="input-field --multi-input">
                    <div>
                      {/* 局數 */}
                      <label>{Multilingual(`GAME.INNINGS`)}</label>
                      <Input
                        width="100%"
                        type="number"
                        name="info.innings"
                        value={currentGameData.info.innings}
                        min="1"
                        onChange={handleInputOnChange}
                        disabled={apiInProgress}
                      />
                    </div>
                  </div>
                  {/* 客場逐局分數、安打 */}
                  <div className="section--description">
                    {`${Multilingual(`TOURNAMENT.SCORE_BOARD_INNINGS_DESCRIPTION`)}
                    ${Multilingual(`TOURNAMENT.SCORE_BOARD_INNINGS_DESCRIPTION_EXAMPLE`)}`}
                  </div>
                  <div className="input-field --multi-input">
                    <div>
                      {/* 局數 */}
                      <label>{`${Multilingual(`GAME.AWAY`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                      <Input
                        width="100%"
                        name="away.scoresString"
                        value={currentGameData.away.scoresString}
                        onChange={handleInputOnChange}
                        disabled={apiInProgress}
                      />
                    </div>
                    <div>
                      {/* 安打 */}
                      <label>{`${Multilingual(`GAME.AWAY`)} ${Multilingual(`GAME.HITS`)}`}</label>
                      <Input
                        width="100%"
                        type="number"
                        name="away.hits"
                        value={currentGameData.away.hits}
                        min="0"
                        onChange={handleInputOnChange}
                        disabled={currentGameData.away.record_battings || apiInProgress}
                      />
                    </div>
                  </div>
                  <div className="input-field --multi-input">
                    <div>
                      {/* 局數 */}
                      <label>{`${Multilingual(`GAME.HOME`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                      <Input
                        width="100%"
                        name="home.scoresString"
                        value={currentGameData.home.scoresString}
                        onChange={handleInputOnChange}
                        disabled={apiInProgress}
                      />
                    </div>
                    <div>
                      {/* 安打 */}
                      <label>{`${Multilingual(`GAME.HOME`)} ${Multilingual(`GAME.HITS`)}`}</label>
                      <Input
                        width="100%"
                        type="number"
                        name="home.hits"
                        value={currentGameData.home.hits}
                        min="0"
                        onChange={handleInputOnChange}
                        disabled={currentGameData.home.record_battings || apiInProgress}
                      />
                    </div>
                  </div>
                  {(currentGameData.away.record_battings || currentGameData.home.record_battings) && (
                    <div className="section--description">
                      {Multilingual(`TOURNAMENT.SCORE_BOARD_HITS_DISABLE_DESCRIPTION`)}
                    </div>
                  )}
                </Fragment>
              )}
            </Fragment>
          )}
          <div className="input-field --multi-input">
            <div>
              <label>觀眾人數</label>
              <Input
                width="100%"
                name="info.audience"
                value={currentGameData.info.audience}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>主審</label>
              <Input
                width="100%"
                name="info.umpire.umpireInChief"
                value={currentGameData.info.umpire.umpireInChief}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>一壘審</label>
              <Input
                width="100%"
                name="info.umpire.fbUmpire"
                value={currentGameData.info.umpire.fbUmpire}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>二壘審</label>
              <Input
                width="100%"
                name="info.umpire.sbUmpire"
                value={currentGameData.info.umpire.sbUmpire}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>三壘審</label>
              <Input
                width="100%"
                name="info.umpire.tbUmpire"
                value={currentGameData.info.umpire.tbUmpire}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>左外野線審</label>
              <Input
                width="100%"
                name="info.umpire.lfUmpire"
                value={currentGameData.info.umpire.lfUmpire}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
            <div>
              <label>右外野線審</label>
              <Input
                width="100%"
                name="info.umpire.rfUmpire"
                value={currentGameData.info.umpire.rfUmpire}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
          </div>
        </div>
      </StyledForm>
    </GameSectionDiv>
  );
};

export default GameScoreBoardSection;
